/**
 * In-house report state. Deliberately ignores `study.status`: that value is
 * derived across BOTH report kinds, so a teleconsultation final would light
 * the in-house glyph. No `reportStatus` means no in-house report.
 */
export function reportGlyphState(study) {
    const reportStatus = study.reportStatus ?? null;
    if (reportStatus === 'final' || reportStatus === 'amended')
        return 'final';
    if (reportStatus === 'draft' || reportStatus === 'preliminary')
        return 'pending';
    return 'none';
}
/**
 * Case statuses that still owe the clinic a report. The same set the backend
 * uses to pick which case `consultationStatus` describes, and the set a
 * clinic may still close from its side (CancelCase refuses anything signed).
 */
const CONSULT_OPEN_STATUSES = new Set([
    'pending',
    'claimed',
    'in_progress',
    'preliminary',
]);
const CONSULT_FINAL_STATUSES = new Set(['final', 'delivered', 'amended']);
/** True while the case is open: requested, claimed, being read, or preliminary. */
export function isOpenConsultStatus(status) {
    return status != null && CONSULT_OPEN_STATUSES.has(status);
}
/** True once the consultation report is signed (final, delivered, or amended). */
export function isFinalConsultStatus(status) {
    return status != null && CONSULT_FINAL_STATUSES.has(status);
}
/**
 * Teleconsultation state from the case status: open cases (requested,
 * claimed, being read, preliminary) are pending; a cancelled case or none at
 * all is nothing.
 *
 * Reads `consultationStatus` alone, which the backend already resolves to the
 * newest OPEN case ahead of any closed one. So a study whose signed consult
 * was followed by a new request lights orange, not purple, and the glyph
 * agrees with the case list in the card without re-deriving precedence here.
 */
export function consultGlyphState(study) {
    const status = study.consultationStatus ?? null;
    if (status === null)
        return 'none';
    if (CONSULT_FINAL_STATUSES.has(status))
        return 'final';
    if (CONSULT_OPEN_STATUSES.has(status))
        return 'pending';
    return 'none';
}
export function consultGlyphDetail(study) {
    const state = consultGlyphState(study);
    const cases = study.consultations ?? [];
    // The newest open case is the one consultationStatus describes; its group
    // is the one the tooltip should name. Older backends send no list, so the
    // tooltip falls back to a wording without a group.
    const open = state === 'pending' ? cases.find((c) => CONSULT_OPEN_STATUSES.has(c.status)) : null;
    return {
        state,
        groupName: open?.groupName?.trim() || null,
        cancelled: state === 'none' && study.consultationStatus === 'cancelled',
    };
}
/** Orange while something is in progress, one "done" colour per report kind. */
export const REPORT_GLYPH_COLOR = {
    final: '#10B981',
    pending: '#FB923C',
};
export const CONSULT_GLYPH_COLOR = {
    final: '#A78BFA',
    pending: '#FB923C',
};
